import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  DEFAULT_LOT_LIST_STATUSES,
  formatTenge,
  LOT_STATUS_LABELS,
  LOT_STATUSES,
  TRANSPORT_KIND_LABELS,
  TRANSPORT_KINDS,
  vatLabel,
  type LotListItem,
  type LotListQuery,
  type LotStatus,
  type ProjectOption,
  type TransportKind,
  type UserOption,
} from "@omts/shared";

/** Цвет бейджа статуса: торги — зелёный, этапы после торгов — синий, оплачено — серый, отмена — красный. */
export function statusBadgeClass(status: LotStatus): string {
  switch (status) {
    case "active":
      return "";
    case "awaiting_winner_data":
    case "formalized_pre_trip":
    case "delivered":
    case "closed":
      return "stage";
    case "cancelled":
    case "no_winner":
      return "danger";
    default:
      return "muted";
  }
}
import { api, subscribeEvents } from "../api";
import { PaymentTasks } from "../components/PaymentTasks";

const fmt = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function timeLeft(deadlineIso: string): string {
  const ms = new Date(deadlineIso).getTime() - Date.now();
  if (ms <= 0) return "срок истёк";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
}

const isStatus = (s: string): s is LotStatus => (LOT_STATUSES as readonly string[]).includes(s);
const isKind = (s: string): s is TransportKind => (TRANSPORT_KINDS as readonly string[]).includes(s);

/**
 * Фильтры живут в URL (16.3), чтобы ссылку можно было отправить коллеге.
 * Нет параметра status — статусы по умолчанию; status= (пусто) — ничего не выбрано.
 */
function parseQuery(params: URLSearchParams): LotListQuery {
  const q: LotListQuery = {};
  if (params.has("status")) q.status = params.getAll("status").flatMap((s) => s.split(",")).filter(isStatus);
  const projectId = params.get("projectId");
  if (projectId) q.projectId = projectId;
  const kind = params.get("transportKind");
  if (kind && isKind(kind)) q.transportKind = kind;
  const from = params.get("loadingFrom");
  if (from) q.loadingFrom = from;
  const to = params.get("loadingTo");
  if (to) q.loadingTo = to;
  const createdBy = params.get("createdBy");
  if (createdBy) q.createdBy = createdBy;
  const text = params.get("q")?.trim();
  if (text) q.q = text;
  return q;
}

function toParams(q: LotListQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.status) {
    if (q.status.length) for (const s of q.status) p.append("status", s);
    else p.set("status", "");
  }
  if (q.projectId) p.set("projectId", q.projectId);
  if (q.transportKind) p.set("transportKind", q.transportKind);
  if (q.loadingFrom) p.set("loadingFrom", q.loadingFrom);
  if (q.loadingTo) p.set("loadingTo", q.loadingTo);
  if (q.createdBy) p.set("createdBy", q.createdBy);
  if (q.q) p.set("q", q.q);
  return p;
}

const sameStatuses = (a: LotStatus[], b: LotStatus[]) => a.length === b.length && a.every((s) => b.includes(s));

export function LotsPage() {
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => parseQuery(params), [params]);
  const queryKey = params.toString();
  const statuses = query.status ?? DEFAULT_LOT_LIST_STATUSES;
  const isDefault = queryKey === "";

  const [lots, setLots] = useState<LotListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [search, setSearch] = useState(query.q ?? "");
  /** Что поле поиска последним записало в URL — чтобы не перетирать ввод пользователя при синхронизации из URL. */
  const pushedQ = useRef(query.q ?? "");

  const update = (patch: Partial<LotListQuery>) => {
    const next: LotListQuery = { ...query, ...patch };
    // Статусы по умолчанию в URL не пишем — ссылка короче, а «Сбросить» однозначен.
    if (next.status && sameStatuses(next.status, DEFAULT_LOT_LIST_STATUSES)) delete next.status;
    setParams(toParams(next), { replace: true });
  };

  const load = () =>
    api
      .lots(query)
      .then((rows) => {
        setLots(rows);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void load();
    // Любое изменение лота на сервере — перезагружаем список (SSE).
    const unsubscribe = subscribeEvents((type) => {
      if (type === "lot.updated") void load();
    });
    const tick = setInterval(() => setLots((l) => (l ? [...l] : l)), 30_000); // обновить таймеры
    return () => {
      unsubscribe();
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  useEffect(() => {
    api.projects().then(setProjects).catch(() => undefined);
    api.users().then(setUsers).catch(() => undefined);
  }, []);

  // Поиск: в URL с задержкой, чтобы не дёргать API на каждую букву. Зависит и от URL: смена фильтров/«Сбросить»
  // отменяет отложенную запись, иначе таймер перезаписал бы URL устаревшим состоянием.
  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === (query.q ?? "")) return;
    const t = setTimeout(() => {
      pushedQ.current = trimmed;
      update({ q: trimmed || undefined });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, queryKey]);
  // Переход по ссылке с другим q (назад/вперёд, «Сбросить») — синхронизируем поле; своё же значение не трогаем.
  useEffect(() => {
    const q = query.q ?? "";
    if (q === pushedQ.current) return;
    pushedQ.current = q;
    setSearch(q);
  }, [query.q]);

  const toggleStatus = (s: LotStatus) =>
    update({ status: statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s] });

  const reset = () => {
    pushedQ.current = "";
    setSearch("");
    setParams(new URLSearchParams(), { replace: true });
  };

  return (
    <>
      <div className="page-header">
        <h1>Заявки</h1>
        <Link to="/lots/new" className="btn primary">
          + Новая заявка
        </Link>
      </div>

      <PaymentTasks />

      <section className="card filters-card" aria-label="Фильтры">
        <div className="filters">
          <label className="f grow">
            Поиск
            <input
              type="search"
              placeholder="Номер, адрес погрузки/выгрузки, груз"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="f">
            Проект
            <select value={query.projectId ?? ""} onChange={(e) => update({ projectId: e.target.value || undefined })}>
              <option value="">Все</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="f">
            Тип перевозки
            <select
              value={query.transportKind ?? ""}
              onChange={(e) => update({ transportKind: isKind(e.target.value) ? e.target.value : undefined })}
            >
              <option value="">Все</option>
              {TRANSPORT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {TRANSPORT_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="f">
            Погрузка с
            <input type="date" value={query.loadingFrom ?? ""} onChange={(e) => update({ loadingFrom: e.target.value || undefined })} />
          </label>
          <label className="f">
            по
            <input type="date" value={query.loadingTo ?? ""} onChange={(e) => update({ loadingTo: e.target.value || undefined })} />
          </label>
          <label className="f">
            Автор
            <select value={query.createdBy ?? ""} onChange={(e) => update({ createdBy: e.target.value || undefined })}>
              <option value="">Все</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                  {u.isActive ? "" : " (неактивен)"}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="statuses" role="group" aria-label="Статусы">
          {LOT_STATUSES.map((s) => (
            <label key={s} className={`chip ${statuses.includes(s) ? "on" : ""}`}>
              <input type="checkbox" checked={statuses.includes(s)} onChange={() => toggleStatus(s)} />
              {LOT_STATUS_LABELS[s]}
            </label>
          ))}
        </div>
        <div className="filters-foot">
          <span className="muted">{lots ? `Найдено: ${lots.length}` : "Загрузка…"}</span>
          {!isDefault && (
            <button type="button" className="btn small" onClick={reset}>
              Сбросить
            </button>
          )}
        </div>
      </section>

      {error && <p className="error">{error}</p>}
      {lots && lots.length === 0 && !error && (
        <p className="muted">{isDefault ? "Заявок пока нет." : "По заданным фильтрам заявок нет."}</p>
      )}

      {(lots ?? []).map((lot) => (
        <Link to={`/lots/${lot.id}`} key={lot.id} className="card lot-row">
          <div>
            <div style={{ fontWeight: 600 }}>{lot.number}</div>
            <div className="sub">{lot.projectName}</div>
          </div>
          <div>
            <div className="route">
              {lot.loadingAddress} → {lot.unloadingAddress}
            </div>
            <div className="sub">
              Погрузка {fmt.format(new Date(lot.loadingAt))} · {lot.vehicleTypeName} · {lot.weightKg.toLocaleString("ru-RU")} кг ·{" "}
              {TRANSPORT_KIND_LABELS[lot.transportKind]}
            </div>
          </div>
          <div>
            {lot.currentMinAmount === null ? (
              <div className="price empty">ставок нет</div>
            ) : (
              <div className="price">{formatTenge(lot.currentMinAmount)}</div>
            )}
            <div className="sub">
              {lot.bidsCount} ставок · {vatLabel(lot.transportKind)}
            </div>
          </div>
          <div>
            <span className={`badge ${statusBadgeClass(lot.status)}`}>{LOT_STATUS_LABELS[lot.status]}</span>
            {lot.status === "active" && (
              <div className="sub">
                до {fmt.format(new Date(lot.deadlineAt))} · {timeLeft(lot.deadlineAt)}
                {lot.autoExtend && ` · автопродление ${lot.autoExtensionsUsed}/2`}
              </div>
            )}
          </div>
          <div className="sub">{lot.createdByName}</div>
        </Link>
      ))}
    </>
  );
}
